import React, { useState, useEffect } from 'react';
import { X, User, Phone, Mail, Clock, FileText, CheckCircle, XCircle, CheckSquare, ExternalLink, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppointmentDayModal = ({ date, onClose, onRefresh }) => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  useEffect(() => {
    fetchAppointments();
  }, [date]);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const dateStr = date.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
      
      const response = await axios.get(`${API}/emr/appointments/date/${encodeURIComponent(dateStr)}`, {
        withCredentials: true
      });
      
      if (response.data.appointments) {
        setAppointments(response.data.appointments);
      }
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    setUpdatingStatus(appointmentId);
    try {
      await axios.patch(
        `${API}/emr/appointments/${appointmentId}/status?status=${newStatus}`,
        {},
        { withCredentials: true }
      );
      
      // Refresh appointments
      await fetchAppointments();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update appointment status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const navigateToPatientChart = async (appointment) => {
    try {
      // Search for patient by phone
      const response = await axios.get(
        `${API}/emr/patients?search=${encodeURIComponent(appointment.patient_phone)}`,
        { withCredentials: true }
      );
      
      if (response.data && response.data.length > 0) {
        // Patient exists, navigate to their chart
        navigate(`/emr/patient/${response.data[0].id}`);
      } else {
        // Patient doesn't exist, ask to create
        if (window.confirm(`Patient record not found. Would you like to create a new patient record for ${appointment.patient_name}?`)) {
          navigate('/emr/patient/new', {
            state: {
              prefill: {
                first_name: appointment.patient_name.split(' ')[0],
                last_name: appointment.patient_name.split(' ').slice(1).join(' '),
                cell_number: appointment.patient_phone,
                email: appointment.patient_email
              }
            }
          });
        }
      }
    } catch (error) {
      console.error('Failed to find patient:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-yellow-500/30">
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-400/10 to-yellow-600/10 border-b border-yellow-500/30 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-yellow-400">Appointments</h2>
              <p className="text-gray-300 mt-1">{formatDate(date)}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-gray-400">Loading appointments...</div>
            </div>
          ) : appointments.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No appointments scheduled for this day</p>
            </div>
          ) : (
            <div className="space-y-4">
              {appointments.map((apt, index) => (
                <div
                  key={apt.id || index}
                  className="bg-black/30 border border-gray-800 rounded-xl p-6 hover:border-yellow-500/30 transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-yellow-400/20 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-yellow-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">{apt.patient_name}</h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-300 font-medium">{apt.appointment_time}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        apt.source === 'booking' 
                          ? 'bg-blue-500/20 text-blue-400' 
                          : 'bg-purple-500/20 text-purple-400'
                      }`}>
                        {apt.source === 'booking' ? '🌐 Online Booking' : '👤 Manual Entry'}
                      </span>
                      {apt.consent_signed && (
                        <span className="flex items-center space-x-1 px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
                          <CheckCircle className="w-3 h-3" />
                          <span>Consent Signed</span>
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center space-x-3">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-300">{apt.patient_phone}</span>
                    </div>
                    {apt.patient_email && (
                      <div className="flex items-center space-x-3">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-300">{apt.patient_email}</span>
                      </div>
                    )}
                  </div>

                  <div className="bg-yellow-400/10 border border-yellow-500/30 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <FileText className="w-5 h-5 text-yellow-400" />
                      <h4 className="font-semibold text-yellow-400">Service</h4>
                    </div>
                    <p className="text-white text-lg font-medium">{apt.service}</p>
                  </div>

                  {apt.notes && (
                    <div className="mt-4 bg-gray-800/50 rounded-lg p-4">
                      <p className="text-sm text-gray-400 mb-1">Notes:</p>
                      <p className="text-gray-300">{apt.notes}</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {/* Status Update Buttons */}
                    {apt.status !== 'completed' && (
                      <button
                        onClick={() => updateAppointmentStatus(apt.id, 'completed')}
                        disabled={updatingStatus === apt.id}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
                      >
                        <CheckSquare className="w-4 h-4" />
                        <span>Mark Completed</span>
                      </button>
                    )}
                    
                    {apt.status !== 'cancelled' && apt.status !== 'completed' && (
                      <button
                        onClick={() => updateAppointmentStatus(apt.id, 'cancelled')}
                        disabled={updatingStatus === apt.id}
                        className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
                      >
                        <XCircle className="w-4 h-4" />
                        <span>Cancel</span>
                      </button>
                    )}

                    {apt.status === 'cancelled' && (
                      <button
                        onClick={() => updateAppointmentStatus(apt.id, 'scheduled')}
                        disabled={updatingStatus === apt.id}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>Reschedule</span>
                      </button>
                    )}

                    {/* Patient Chart Link */}
                    <button
                      onClick={() => navigateToPatientChart(apt)}
                      className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-black text-sm font-semibold rounded-lg transition-colors ml-auto"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>View Patient Chart</span>
                    </button>
                  </div>

                  {/* Status Badge */}
                  <div className="mt-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                      apt.status === 'completed' 
                        ? 'bg-green-500/20 text-green-400' 
                        : apt.status === 'cancelled'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      STATUS: {(apt.status || 'scheduled').toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-800 p-4 bg-gray-900/50">
          <div className="flex justify-between items-center">
            <div className="text-gray-400 text-sm">
              Total: {appointments.length} appointment{appointments.length !== 1 ? 's' : ''}
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-800 hover:bg-gray-700 text-white font-semibold rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentDayModal;
